import type { UnresolvedReason } from "./verdict.js";

/**
 * The one source of customer-facing failure text.
 *
 * Consumed by both the internal decision path (`functions/src/qualify.ts`,
 * re-exported from there for `functions/src/http/resolveAddress.ts` and
 * `submitLead.ts`) and the public widget's mock mode (`web/src/lib/api.ts`).
 * Two copies of this map would drift, and the drift would only ever be
 * visible to a customer, so it lives here where both workspaces already
 * depend on it.
 *
 * Deliberately free of internal detail and of the submitted address, because
 * these strings are shown to a person and, once the widget ships, may end up
 * in a screenshot.
 */
export const UNRESOLVED_MESSAGES: Record<UnresolvedReason, string> = {
  address_not_found: "We could not match that address. We'll verify it manually and follow up.",
  out_of_state:
    "That address is outside New York State, so the NYSERDA Disadvantaged Community program does not apply.",
  // Deliberately says nothing about the designation or the incentive, because we
  // are declining to answer rather than reporting a result. It also promises no
  // follow-up: unlike the three deferred reasons, no lead is written here, so a
  // promise would be one nobody could keep. The advisor button is on this screen
  // already for anyone who wants a person anyway.
  outside_service_area:
    "Kasselman Solar does not currently service Long Island or New York City, so we cannot check this address.",
  tract_not_in_dataset:
    "We matched that address but it is not in our current dataset. We'll verify it manually and follow up.",
  geocoder_unavailable:
    "Our address lookup is temporarily unavailable. We'll verify this manually and follow up.",
};

/**
 * The customer-facing sentence for an unresolved reason.
 *
 * @param reason  The arm the resolver settled on.
 * @returns       Text safe to render to a person. Never contains internal detail
 *                or the submitted address.
 * @see docs/design.md#which-reasons-stop-the-flow
 */
export function unresolvedMessage(reason: UnresolvedReason): string {
  return UNRESOLVED_MESSAGES[reason];
}
